<template>
  <div class="device-page">
    <HeadNav
      selected-language="en"
      page="devices"
      :gotodownload="gotodownload"
    />

    <main class="device-main">
      <nav class="breadcrumb" aria-label="Breadcrumb">
        <a href="/">Home</a>
        <span aria-hidden="true">/</span>
        <span>Devices</span>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{{ page.h1 }}</span>
      </nav>

      <section class="device-hero">
        <div class="device-hero-copy">
          <p class="device-eyebrow">Device guide</p>
          <h1>{{ page.h1 }}</h1>
          <h2>{{ page.heroTitle }}</h2>
          <p>{{ page.heroText }}</p>
          <a class="primary-download" href="/download-apk/">
            <i class="fa fa-cloud-download-alt" aria-hidden="true"></i>
            {{ page.ctaLabel }}
          </a>
        </div>
        <div
          v-if="page.heroFacts && page.heroFacts.length"
          class="device-hero-facts"
        >
          <i
            :class="['fa', page.heroIcon, 'device-hero-icon']"
            aria-hidden="true"
          ></i>
          <p class="device-hero-facts-title">Platform overview</p>
          <ul>
            <li v-for="fact in page.heroFacts" :key="fact">
              <i class="fa fa-check" aria-hidden="true"></i>
              <span>{{ fact }}</span>
            </li>
          </ul>
        </div>
      </section>

      <aside v-if="page.notice" class="device-notice" role="note">
        <i class="fa fa-info-circle" aria-hidden="true"></i>
        <p>{{ page.notice }}</p>
      </aside>

      <article class="device-content">
        <section
          v-for="section in page.sections"
          :key="section.heading"
          class="device-section"
        >
          <h2>{{ section.heading }}</h2>
          <p
            v-for="(paragraph, index) in section.paragraphs"
            :key="`${section.heading}-${index}`"
            v-html="formatText(paragraph)"
          ></p>
          <ol
            v-if="section.items && section.items.length && section.ordered"
            class="guide-list guide-list--ordered"
          >
            <li
              v-for="item in section.items"
              :key="item"
              v-html="formatText(item)"
            ></li>
          </ol>
          <ul
            v-else-if="section.items && section.items.length"
            class="guide-list"
          >
            <li
              v-for="item in section.items"
              :key="item"
              v-html="formatText(item)"
            ></li>
          </ul>
          <p
            v-for="(paragraph, index) in section.trailingParagraphs"
            :key="`${section.heading}-trailing-${index}`"
            v-html="formatText(paragraph)"
          ></p>
          <ol v-if="section.steps && section.steps.length" class="step-list">
            <li v-for="step in section.steps" :key="step.title">
              <div>
                <h3>{{ step.title }}</h3>
                <p>{{ step.text }}</p>
              </div>
            </li>
          </ol>
          <div v-if="section.table" class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th
                    v-for="header in section.table.headers"
                    :key="header"
                    scope="col"
                  >{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in section.table.rows" :key="row[0]">
                  <th scope="row">{{ row[0] }}</th>
                  <td v-for="cell in row.slice(1)" :key="cell">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </article>

      <section class="faq-section" aria-labelledby="device-faq">
        <p class="section-kicker">Common questions</p>
        <h2 id="device-faq">FAQ</h2>
        <div class="faq-list">
          <div
            v-for="(faq, index) in page.faqs"
            :key="faq.question"
            class="faq-item"
            :class="{ 'is-open': openFaq === index }"
          >
            <button
              type="button"
              class="faq-question"
              :aria-expanded="openFaq === index ? 'true' : 'false'"
              @click="toggleFaq(index)"
            >
              <span>{{ faq.question }}</span>
              <i class="fa fa-chevron-down" aria-hidden="true"></i>
            </button>
            <div class="faq-answer">
              <p>{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="related-section" aria-labelledby="related-devices">
        <h2 id="related-devices">Recommended Devices</h2>
        <div class="related-links">
          <a
            v-for="related in page.related"
            :key="related.href"
            :href="related.href"
          >
            <span>{{ related.label }}</span>
            <i class="fa fa-arrow-right" aria-hidden="true"></i>
          </a>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';
import devicePages from '@/constants/devicePages';

export default {
  name: 'DevicePage',
  components: {
    HeadNav,
  },
  data() {
    return {
      openFaq: 0,
    };
  },
  computed: {
    page() {
      const slug = this.$route.meta.deviceSlug;
      return devicePages.find((item) => item.slug === slug) || devicePages[0];
    },
  },
  watch: {
    '$route.fullPath'() {
      this.openFaq = 0;
    },
  },
  methods: {
    formatText(value) {
      if (typeof value !== 'string') {
        return '';
      }

      const escaped = value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

      return escaped
        .replace(
          /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
          '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
        )
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    },
    toggleFaq(index) {
      this.openFaq = this.openFaq === index ? -1 : index;
    },
    gotodownload() {
      if (typeof window !== 'undefined') {
        window.location.href = '/download-apk/';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.device-page {
  min-height: 100vh;
  background: #f5f8f7;
  color: #14213d;
}

.device-main {
  width: min(1050px, calc(100% - 32px));
  margin: 0 auto;
  padding: 24px 0 64px;
}

.breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 4px 0 18px;
  color: #64748b;
  font-size: 13px;

  a {
    color: #0b6d47;
    font-weight: 600;
    text-decoration: none;
  }
}

.device-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(260px, 0.55fr);
  gap: 36px;
  align-items: center;
  padding: 40px;
  border: 1px solid #dcebe4;
  border-radius: 8px;
  background:
    linear-gradient(120deg, rgba(255, 255, 255, 0.98), rgba(237, 249, 243, 0.96)),
    #fff;
  box-shadow: 0 10px 28px rgba(23, 70, 52, 0.06);
}

.device-eyebrow,
.section-kicker {
  margin: 0 0 10px;
  color: #0b6d47;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.device-hero h1 {
  margin: 0;
  color: #102b21;
  font-size: clamp(34px, 5vw, 50px);
  line-height: 1.12;
  letter-spacing: -0.02em;
}

.device-hero h2 {
  margin: 12px 0 0;
  color: #0b6d47;
  font-size: 20px;
  line-height: 1.4;
}

.device-hero-copy > p:not(.device-eyebrow) {
  margin: 16px 0 0;
  color: #50645c;
  font-size: 16px;
  line-height: 1.75;
}

.primary-download {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 50px;
  margin-top: 24px;
  padding: 13px 22px;
  border-radius: 7px;
  background: #0b6d47;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 8px 22px rgba(11, 109, 71, 0.18);
}

.primary-download:hover,
.primary-download:focus {
  background: #0a5e3d;
  color: #fff;
}

.device-hero-facts {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px 0 8px 30px;
  border-left: 2px solid #cfe8dc;
}

.device-hero-icon {
  color: #0b6d47;
  font-size: 46px;
  line-height: 1;
}

.device-hero-facts-title {
  margin: 18px 0 0;
  color: #173b2e;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.device-hero-facts ul {
  margin: 13px 0 0;
  padding: 0;
  list-style: none !important;
}

.device-hero-facts li {
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr);
  gap: 9px;
  align-items: start;
  margin-top: 10px;
  color: #41554d;
  font-size: 14px;
  line-height: 1.55;
}

.device-hero-facts li .fa {
  margin-top: 4px;
  color: #24a36a;
  font-size: 10px;
}

.device-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 18px;
  padding: 17px 20px;
  border: 1px solid #f0d7a6;
  border-radius: 8px;
  background: #fffaf0;
  color: #72501b;

  .fa {
    margin-top: 3px;
    color: #b97913;
  }

  p {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.65;
  }
}

.device-content {
  margin-top: 20px;
  padding: 12px 42px 36px;
  border: 1px solid #dfe9e4;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(28, 67, 53, 0.05);
}

.device-section {
  padding-top: 30px;

  & + .device-section {
    margin-top: 30px;
    border-top: 1px solid #edf2ef;
  }

  h2 {
    margin: 0 0 14px;
    color: #173b2e;
    font-size: 25px;
    line-height: 1.35;
  }

  p {
    margin: 0 0 15px;
    color: #41554d;
    font-size: 16px;
    line-height: 1.8;
  }

  p:last-child {
    margin-bottom: 0;
  }
}

.device-section ::v-deep strong {
  font-weight: 700 !important;
}

.device-section ::v-deep a {
  color: #0b6d47;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.guide-list {
  margin: 10px 0 4px;
  padding: 0;
  list-style: none !important;
}

.guide-list li {
  position: relative;
  margin-bottom: 10px;
  padding-left: 24px;
  color: #41554d;
  font-size: 16px;
  line-height: 1.7;
}

.guide-list li::before {
  position: absolute;
  top: 0.78em;
  left: 2px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #28a873;
  content: "";
  transform: translateY(-50%);
}

.guide-list--ordered {
  padding-left: 24px;
  list-style: decimal !important;
}

.guide-list--ordered li {
  padding-left: 4px;
}

.guide-list--ordered li::before {
  display: none;
}

.guide-list + p {
  margin-top: 14px;
}

.step-list {
  margin: 18px 0 0;
  padding: 0;
  list-style: none !important;
  list-style-type: none !important;
}

.step-list > li::marker {
  content: "";
}

.step-list > li {
  margin-bottom: 22px;

  &:last-child {
    margin-bottom: 0;
  }

  h3 {
    margin: 1px 0 5px;
    color: #173b2e;
    font-size: 17px;
    line-height: 1.4;
  }

  p {
    margin: 0;
    font-size: 15px;
  }
}

.table-wrap {
  overflow-x: auto;
  margin-top: 18px;
  border: 1px solid #dfe9e4;
  border-radius: 7px;
}

table {
  width: 100%;
  min-width: 620px;
  border-collapse: collapse;
}

th,
td {
  padding: 14px 16px;
  border-bottom: 1px solid #e7efeb;
  color: #41554d;
  font-size: 14px;
  line-height: 1.6;
  text-align: left;
  vertical-align: top;
}

thead th {
  background: #edf9f3;
  color: #173b2e;
  font-size: 13px;
}

tbody th {
  width: 24%;
  background: #f8fbf9;
  color: #173b2e;
}

tbody tr:last-child th,
tbody tr:last-child td {
  border-bottom: 0;
}

.faq-section,
.related-section {
  margin-top: 20px;
  padding: 30px 34px;
  border: 1px solid #dfe9e4;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 7px 22px rgba(28, 67, 53, 0.04);
}

.faq-section h2,
.related-section h2 {
  margin: 0;
  color: #173b2e;
  font-size: 25px;
  line-height: 1.35;
}

.faq-list {
  display: grid;
  gap: 10px;
  margin-top: 18px;
}

.faq-item {
  overflow: hidden;
  border: 1px solid #dbe8e1;
  border-radius: 7px;
  background: #fff;
}

.faq-question {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 16px 18px;
  border: 0;
  background: transparent;
  color: #173b2e;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.45;
  text-align: left;
  cursor: pointer;

  .fa {
    flex: 0 0 auto;
    color: #0b6d47;
    transition: transform 0.2s ease;
  }
}

.faq-item.is-open .faq-question .fa {
  transform: rotate(180deg);
}

.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.25s ease;

  p {
    margin: 0;
    padding: 0 18px 17px;
    color: #53665e;
    font-size: 15px;
    line-height: 1.7;
  }
}

.faq-item.is-open .faq-answer {
  max-height: 300px;
}

.related-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;
}

.related-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 10px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    padding: 10px 15px;
    border: 1px solid #cfe4d9;
    border-radius: 7px;
    background: #f6fbf8;
    color: #0b6d47;
    font-size: 13px;
    font-weight: 800;
    text-decoration: none;
  }
}

@media (max-width: 800px) {
  .device-hero {
    grid-template-columns: 1fr;
  }

  .device-hero-facts {
    padding: 24px 0 0;
    border-top: 1px solid #cfe8dc;
    border-left: 0;
  }

  .related-section {
    align-items: stretch;
  }

  .related-links {
    justify-content: flex-start;
  }
}

@media (max-width: 560px) {
  .device-main {
    width: min(100% - 20px, 1050px);
    padding-top: 16px;
  }

  .device-hero {
    gap: 26px;
    padding: 30px 20px;
  }

  .device-hero h1 {
    font-size: 34px;
  }

  .primary-download {
    width: 100%;
  }

  .device-content {
    padding: 4px 20px 28px;
  }

  .device-section h2 {
    font-size: 22px;
  }

  .faq-section,
  .related-section {
    padding: 24px 20px;
  }

  .related-links {
    flex-direction: column;

    a {
      justify-content: space-between;
      width: 100%;
    }
  }
}
</style>
